import Icon from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import AddIcon from "rivals/components/shared/Icons/Add";
import CheckMark from "rivals/components/shared/Icons/CheckMark";
import RemoveIcon from "rivals/components/shared/Icons/Remove";
import ProductCard from "rivals/components/shared/Subscription/ProductCard";
import { sendToAirbrake } from "rivals/redux/error/actions";
import { setSelectedAddOn } from "rivals/redux/registration/actions";
import { selectAddOnStatus } from "rivals/redux/registration/selectors";
import { AddOns } from "rivals/shared/interfaces/AddOn";
import styles from "rivals/styles/exports.module.scss";
import strings from "./strings";
import { Props } from "./types";

const { add, added, included, remove } = strings;

function formatDescription(description: string): JSX.Element {
  return <div dangerouslySetInnerHTML={{ __html: description }} />;
}

interface CtaProps {
  ctaIcon?: JSX.Element;
  ctaText: string;
}

/**
 * Ad-Lite cta values vary depending on the state of All Access since it incluces Ad-Lite
 *
 * @param isSelected
 * @param isDisabled
 * @returns valid CtaProps based on the currently selected values
 */
function adLiteCtaValues(isSelected: boolean, isDisabled: boolean): CtaProps {
  const ctaProps: CtaProps = {
    ctaIcon: undefined,
    ctaText: ""
  };

  if (isSelected) {
    ctaProps.ctaIcon = <RemoveIcon fill={styles.abyss} />;
    ctaProps.ctaText = remove;
  } else if (isDisabled) {
    ctaProps.ctaText = included;
  } else {
    ctaProps.ctaIcon = <AddIcon fill={styles.abyss} />;
    ctaProps.ctaText = add;
  }

  return ctaProps;
}

/**
 * Wrapper around ProductCard to parse redux props for subscription page
 */
function AddOnOffer({ addOn }: Props): JSX.Element | null {
  const dispatch = useDispatch();
  const selectedAddOns = useSelector(selectAddOnStatus);

  const headline = addOn.title;
  const description = formatDescription(addOn.summary);
  const price = addOn.purchasePrice || addOn.price;
  let ctaIcon;
  let ctaText = "";
  let name: AddOns | null = null;
  let isSelected = false;
  let inactive = false;
  switch (addOn.type) {
    case "ALL_ACCESS":
      name = AddOns.ALL_ACCESS;
      isSelected = selectedAddOns[name];
      ctaIcon = isSelected ? (
        <RemoveIcon fill={styles.abyss} />
      ) : (
        <AddIcon fill={styles.abyss} />
      );
      ctaText = isSelected ? remove : add;
      inactive = selectedAddOns[AddOns.AD_LITE];
      break;
    case "AD_LITE": {
      name = AddOns.AD_LITE;
      isSelected = selectedAddOns[name];
      inactive = selectedAddOns[AddOns.ALL_ACCESS];
      const ctaProps = adLiteCtaValues(isSelected, inactive);
      ctaIcon = ctaProps.ctaIcon;
      ctaText = ctaProps.ctaText;
      break;
    }
    default:
      dispatch(
        sendToAirbrake(
          `Attempted to display unsupported Add On type: ${addOn.type}`
        )
      );
  }

  if (!name) {
    return null;
  }

  function onToggleAddOn(): void {
    if (name) {
      dispatch(setSelectedAddOn(name));
    }
  }

  return (
    <ProductCard
      active={isSelected}
      body={description}
      ctaCallback={onToggleAddOn}
      ctaIcon={ctaIcon}
      ctaText={ctaText}
      headline={headline}
      inactive={inactive}
      indicatorIcon={<Icon component={CheckMark} rev="" translate="yes" />}
      indicatorText={added}
      philter={name.toLowerCase()}
      price={price}
    />
  );
}

export default AddOnOffer;
